/**
 * Post-processing applied to an AI-generated practice plan — water breaks,
 * duration normalization, structural validation/repair, step-name cleanup,
 * the football "Team Competition" filler period, and coach-mandatory-drill
 * enforcement.
 *
 * Previously lived only in src/services/aiService.simplified.ts (the Coach
 * Steve chat generation path) and was entirely missing from the
 * generate-practice-plan edge function (the practice-page job-queue path),
 * so a plan's quality differed depending on which path generated it
 * (sc-276 D8). Single source of truth for both now.
 */

import { isCooldownSegment, isDebriefSegment, isWarmupSegment, isWaterBreakSegment } from './practiceSegments.ts';

/**
 * Loosely-typed mirror of src/services/aiService.types.ts's `GeneratedPracticePlan`
 * (the real definition can't be imported here — it's part of the client-only Vite
 * bundle and pulls in `Sport`/`Team` from src/types). All of the logic below already
 * treats plan contents as effectively `any` internally, matching the original
 * client implementation this was ported from — this alias exists only so the two
 * or three externally-visible signatures below read clearly.
 */
// deno-lint-ignore no-explicit-any
export type GeneratedPracticePlan = { practice: any; drills: any[] };

/**
 * Sports the TEAM_COMPETITION templates below are appropriate for. The templates
 * are football-specific (Ultimate Football, Powerball, Capture the Flag), so they
 * must not be injected into a basketball, tennis or baseball practice.
 */
export const TEAM_COMPETITION_SPORTS = new Set(['Football', 'FlagFootball']);

const TEAM_COMPETITION_TEMPLATES: Array<{ name: string; setup: string; execution: string; coachingPoints: string[] }> = [
  {
    name: 'Ultimate Football',
    setup: 'Divide team into two equal squads. Set up a field with end zones at each end. Use a football.',
    execution: 'Teams advance the ball by passing. Players cannot run with the ball. Defense tries to intercept or knock down passes. Score by catching a pass in the end zone.',
    coachingPoints: [
      'Encourage teamwork and communication',
      'Keep energy high',
      'Focus on fun while maintaining intensity',
      'Celebrate good plays from both teams',
    ],
  },
  {
    name: 'Tug of War Tournament',
    setup: 'Set up a tug of war rope with a center marker. Divide team into position groups or mixed teams.',
    execution: 'Teams compete in a tournament-style competition. Winners advance to the next round until one team is crowned champion.',
    coachingPoints: [
      'Emphasize proper technique and body positioning',
      'Coordinate pulling efforts with teammates',
      'Build team camaraderie through friendly competition',
      'Celebrate effort regardless of outcome',
    ],
  },
  {
    name: 'Powerball Challenge',
    setup: 'Set up two goals/end zones. Use a medicine ball or heavy ball. Divide into two teams.',
    execution: "Teams try to advance the ball to score in the opposing team's goal. Can pass, carry, or roll the ball. Defense tries to gain possession.",
    coachingPoints: [
      'Use proper form when handling the heavy ball',
      'Communicate with teammates',
      'Develop strategies for advancing the ball',
      'Maintain high energy throughout the game',
    ],
  },
  {
    name: 'Capture the Flag',
    setup: "Divide field in half with a centerline. Each team has a 'flag' in their territory. Divide into two teams.",
    execution: "Teams try to capture the opponent's flag and return it to their side without being tagged. If tagged in opponent's territory, player goes to 'jail' until freed by a teammate.",
    coachingPoints: [
      'Develop offensive and defensive strategies',
      'Use speed, agility, and evasive techniques',
      'Communicate and coordinate with teammates',
      'Think strategically about when to attack vs. defend',
    ],
  },
  {
    name: 'Team Relay Challenge',
    setup: 'Set up a relay course with football-specific movements (shuttle run, bear crawl, high knees, backpedal, etc.). Divide into position groups.',
    execution: 'Teams compete in relay race format. Each player completes the course before tagging the next teammate.',
    coachingPoints: [
      'Focus on proper technique while maintaining speed',
      'Encourage and support teammates',
      'Maintain focus and intensity throughout the relay',
      'Celebrate team accomplishments',
    ],
  },
];

function buildTeamCompetitionDrill(): Record<string, unknown> {
  const template = TEAM_COMPETITION_TEMPLATES[Math.floor(Math.random() * TEAM_COMPETITION_TEMPLATES.length)];
  return {
    name: 'Team Competition',
    duration: 10,
    description: `Fun, high-energy team competition: ${template.name}`,
    focus_area: 'Team',
    position_groups: ['ALL'],
    steps: [
      {
        name: 'Team Competition Setup',
        setup: template.setup,
        execution: template.execution,
        coachingPoints: template.coachingPoints,
      },
    ],
  };
}

/** Ensure there's a Team Competition period in the plan. No-ops for sports the football-flavored templates don't suit. */
// deno-lint-ignore no-explicit-any
export function ensureTeamCompetitionPeriod(plan: any, sport?: string): void {
  if (!TEAM_COMPETITION_SPORTS.has(sport ?? '')) return;

  // deno-lint-ignore no-explicit-any
  const competitionDrills = plan.drills.filter((drill: any) =>
    (drill.name && drill.name.toLowerCase().includes('competition')) ||
    (drill.focus_area && drill.focus_area.toString().toLowerCase().includes('competition')) ||
    (drill.description && drill.description.toLowerCase().includes('competition'))
  );

  if (competitionDrills.length === 0) {
    const teamCompDrill = buildTeamCompetitionDrill();
    // deno-lint-ignore no-explicit-any
    const cooldownIndex = plan.drills.findIndex((drill: any) =>
      drill.name && drill.name.toLowerCase().includes('cool') && drill.name.toLowerCase().includes('down')
    );
    if (cooldownIndex !== -1) {
      plan.drills.splice(cooldownIndex, 0, teamCompDrill);
    } else {
      plan.drills.push(teamCompDrill);
    }
    return;
  }

  // deno-lint-ignore no-explicit-any
  const inappropriate = competitionDrills.some((drill: any) => {
    const name = (drill.name || '').toLowerCase();
    const desc = (drill.description || '').toLowerCase();
    return name.includes('7-on-7') || name.includes('scrimmage') || desc.includes('7-on-7') || desc.includes('scrimmage');
  });
  if (!inappropriate) return;

  // deno-lint-ignore no-explicit-any
  const badDrillIndex = plan.drills.findIndex((drill: any) => {
    const name = (drill.name || '').toLowerCase();
    const desc = (drill.description || '').toLowerCase();
    return (
      (name.includes('competition') || desc.includes('competition')) &&
      (name.includes('7-on-7') || name.includes('scrimmage') || desc.includes('7-on-7') || desc.includes('scrimmage'))
    );
  });
  if (badDrillIndex !== -1) {
    plan.drills[badDrillIndex] = buildTeamCompetitionDrill();
  }
}

/** Normalize a drill's `position_groups` into `{ group_name, steps }[]`, optionally merging everything into one 'ALL' group. */
export function normalizePositionGroupArray(
  // deno-lint-ignore no-explicit-any
  positionGroups: any,
  forceAll: boolean
  // deno-lint-ignore no-explicit-any
): Array<{ group_name: string; steps: any[] }> {
  if (forceAll) {
    // deno-lint-ignore no-explicit-any
    const mergedSteps: any[] = [];
    if (Array.isArray(positionGroups)) {
      for (const pg of positionGroups) {
        if (pg && typeof pg === 'object' && Array.isArray(pg.steps)) {
          mergedSteps.push(...pg.steps);
        }
      }
    }
    return [{ group_name: 'ALL', steps: mergedSteps }];
  }

  if (!Array.isArray(positionGroups) || positionGroups.length === 0) {
    return [{ group_name: 'ALL', steps: [] }];
  }

  // deno-lint-ignore no-explicit-any
  return positionGroups.map((pg: any) =>
    typeof pg === 'string'
      ? { group_name: pg, steps: [] }
      : { group_name: pg.group_name || 'ALL', steps: Array.isArray(pg.steps) ? pg.steps : [] }
  );
}

export function buildHydrationBreakName(index: number): string {
  return `Hydration Break ${index}`;
}

/** Ensure there are enough water breaks in the plan, spread out and not clustered. */
// deno-lint-ignore no-explicit-any
export function ensureWaterBreaks(plan: any): any {
  const waterBreaks = plan.drills.filter((drill: unknown) => isWaterBreakSegment(drill as { name?: unknown; description?: unknown }));

  // deno-lint-ignore no-explicit-any
  const totalDuration = plan.drills.reduce((sum: number, drill: any) => sum + drill.duration, 0);
  const targetBreaks = Math.max(1, Math.floor(totalDuration / 35)); // At least one logical break for normal practice lengths
  const minimumBreaks = totalDuration >= 45 ? 1 : 0;
  const finalTargetBreaks = Math.max(minimumBreaks, targetBreaks);

  if (waterBreaks.length < finalTargetBreaks) {
    const drillCount = plan.drills.length;
    const interval = Math.floor(drillCount / (finalTargetBreaks + 1));

    // deno-lint-ignore no-explicit-any
    const newDrills: any[] = [...plan.drills];
    let addedBreaks = 0;

    const buildWaterBreak = (index: number) => ({
      name: buildHydrationBreakName(index),
      duration: 5,
      description: 'Hydration and rest period',
      focus_area: 'Team',
      intensity_level: undefined, // Water breaks don't have an intensity level
      position_groups: ['ALL'],
      steps: [
        {
          name: 'Hydration',
          setup: 'Water bottles available for all players',
          execution: 'Players hydrate and take a brief rest',
          coachingPoints: ['Ensure all players hydrate properly', 'Review upcoming drills if needed'],
        },
      ],
    });

    for (let i = 1; i <= finalTargetBreaks && addedBreaks < (finalTargetBreaks - waterBreaks.length); i++) {
      const insertPosition = i * interval + addedBreaks; // Adjust for previously added breaks

      if (insertPosition >= newDrills.length) {
        newDrills.push(buildWaterBreak(waterBreaks.length + addedBreaks + 1));
        addedBreaks++;
        continue;
      }

      const hasNearbyWaterBreak = newDrills
        .slice(Math.max(0, insertPosition - 2), Math.min(newDrills.length, insertPosition + 2))
        .some((drill) => isWaterBreakSegment(drill as { name?: unknown; description?: unknown }));

      if (!hasNearbyWaterBreak) {
        newDrills.splice(insertPosition, 0, buildWaterBreak(waterBreaks.length + addedBreaks + 1));
        addedBreaks++;
      }
    }

    plan.drills = newDrills;
  }

  return plan;
}

/** Normalize drill durations so total equals targetDurationMinutes. Adjusts the last non-water-break drill to absorb the difference. */
export function normalizeDrillDurations(plan: GeneratedPracticePlan, targetDurationMinutes: number): void {
  // deno-lint-ignore no-explicit-any
  const drills = plan.drills as any[];
  if (!drills.length) return;
  // deno-lint-ignore no-explicit-any
  const total = drills.reduce((sum: number, d: any) => sum + (d.duration || 0), 0);
  const diff = targetDurationMinutes - total;
  if (diff === 0) return;

  let lastIdx = -1;
  for (let i = drills.length - 1; i >= 0; i--) {
    if (!isWaterBreakSegment(drills[i])) {
      lastIdx = i;
      break;
    }
  }
  if (lastIdx === -1) return;
  const d = drills[lastIdx];
  const current = d.duration || 0;
  d.duration = Math.max(1, current + diff);
}

/** Validate/repair the plan's overall structure — missing fields, nested drills, position groups, intensity levels, step defaults. */
// deno-lint-ignore no-explicit-any
export function validateAndFixPlan(planJson: any, sport?: string): GeneratedPracticePlan {
  try {
    if (!planJson.practice) {
      planJson.practice = {
        team_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '11:00',
        location: 'Main Field',
        theme: 'Team Day',
        drills: [], // This will be removed
      };
    }

    if (!planJson.drills) {
      planJson.drills = [];
    }

    // Remove debrief-only segments; cooldowns should be kept as team-wide moments.
    // deno-lint-ignore no-explicit-any
    planJson.drills = planJson.drills.filter((d: any) => !isDebriefSegment(d));

    // Handle case where drills got nested inside practice
    if (Array.isArray(planJson.practice.drills) && planJson.practice.drills.length > 0) {
      if (planJson.drills.length === 0) {
        planJson.drills = planJson.practice.drills;
      }
      delete planJson.practice.drills;
    }

    const practice = planJson.practice;
    if (!practice.team_id) practice.team_id = '';
    if (!practice.date) practice.date = new Date().toISOString().split('T')[0];
    if (!practice.start_time) practice.start_time = '09:00';
    if (!practice.end_time) practice.end_time = '11:00';
    if (!practice.location) practice.location = 'Main Field';
    if (!practice.theme) practice.theme = 'Team Day';

    // deno-lint-ignore no-explicit-any
    planJson.drills = planJson.drills.map((drill: any) => {
      if (!drill.name) drill.name = 'Unnamed Drill';
      if (!drill.duration) drill.duration = 10;
      if (!drill.description) drill.description = '';
      if (!drill.focus_area) drill.focus_area = 'Team';
      if (!drill.position_groups) drill.position_groups = ['ALL'];
      if (!drill.steps || !Array.isArray(drill.steps)) drill.steps = [];

      const isWarmup = isWarmupSegment(drill);
      const isWaterBreak = isWaterBreakSegment(drill);
      const isCooldown = isCooldownSegment(drill);
      const forceAll = isWarmup || isWaterBreak || isCooldown;
      drill.position_groups = normalizePositionGroupArray(drill.position_groups, forceAll);

      const drillNameLower = (drill.name || '').toLowerCase();
      const descLower = (drill.description || '').toLowerCase();
      const isCompetitive = drillNameLower.includes('competitive') || descLower.includes('competitive');

      if (isCompetitive) {
        drill.intensity_level = 'Game speed';
      } else if (drill.intensity_level === undefined || drill.intensity_level === null) {
        drill.intensity_level = isWaterBreak ? null : 'Technique focused';
      }

      // deno-lint-ignore no-explicit-any
      drill.steps = drill.steps.map((step: any) => {
        if (!step.name) step.name = 'Unnamed Step';
        if (!step.setup) step.setup = 'No setup provided';
        if (!step.execution) step.execution = 'No execution instructions provided';
        if (!step.coachingPoints || !Array.isArray(step.coachingPoints)) {
          step.coachingPoints = ['Focus on proper form'];
        }
        return step;
      });

      return drill;
    });

    planJson = ensureWaterBreaks(planJson);
    ensureTeamCompetitionPeriod(planJson, sport);

    return {
      practice: {
        ...planJson.practice,
        team_id: planJson.practice.team_id || '',
      },
      drills: planJson.drills || [],
    };
  } catch (error) {
    console.error('Error in validateAndFixPlan:', error);
    return {
      practice: {
        team_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '11:00',
        location: 'Main Field',
        theme: 'Team Day',
      },
      drills: [],
    };
  }
}

/** Strip leading "1)", "Step 2:", "Option 3", "- " etc. numbering/prefixes from a step name. */
export function removeStepNumbering(stepName: string): string {
  const cleaned = stepName
    .replace(/^\s*(step\s*)?\d+[)\].:-]?\s*/i, '')
    .replace(/^\s*option\s*\d*[)\].:-]?\s*/i, '')
    .replace(/^\s*alternative\s*option\s*\d*[)\].:-]?\s*/i, '')
    .replace(/^\s*[-*]\s*/, '')
    .trim();
  return cleaned || 'Drill Step';
}

/** Clean step names and cap/pad a non-warmup/water/cooldown drill's steps to exactly targetStepCount. */
// deno-lint-ignore no-explicit-any
export function normalizeStepOptionsForDrill(drill: any, targetStepCount: number): any {
  if (isWarmupSegment(drill) || isWaterBreakSegment(drill) || isCooldownSegment(drill)) {
    return drill;
  }

  const normalized = { ...drill };
  if (!Array.isArray(normalized.position_groups)) {
    normalized.position_groups = [{ group_name: 'ALL', steps: [] }];
  }

  // deno-lint-ignore no-explicit-any
  normalized.position_groups = normalized.position_groups.map((group: any) => {
    const steps = Array.isArray(group.steps) ? [...group.steps] : [];
    // deno-lint-ignore no-explicit-any
    const cleaned = steps.map((step: any) => ({
      ...step,
      name: removeStepNumbering(step?.name || 'Drill Option'),
    }));

    const capped = cleaned.slice(0, targetStepCount);
    while (capped.length < targetStepCount) {
      capped.push({
        name: `Alternative Option ${capped.length + 1}`,
        setup: 'Use standard setup for this drill segment.',
        execution: 'Choose one or two options that best fit today and save remaining options for a future practice.',
        coachingPoints: ['Prioritize quality reps over volume'],
        equipment: '',
      });
    }

    return { ...group, steps: capped };
  });

  return normalized;
}

/** Unconditional final pass: clean every step name in every position group, including warmup/water/cooldown. */
// deno-lint-ignore no-explicit-any
export function sanitizeAllStepNames(drill: any): any {
  const normalized = { ...drill };
  if (!Array.isArray(normalized.position_groups)) return normalized;
  // deno-lint-ignore no-explicit-any
  normalized.position_groups = normalized.position_groups.map((group: any) => {
    const steps = Array.isArray(group.steps) ? group.steps : [];
    return {
      ...group,
      // deno-lint-ignore no-explicit-any
      steps: steps.map((step: any) => ({ ...step, name: removeStepNumbering(step?.name || '') })),
    };
  });
  return normalized;
}

function tokenizeOutlinePhrase(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 1);
}

function outlineCoversMandatoryPhrase(outline: GeneratedPracticePlan, mandatory: string): boolean {
  const mTokens = tokenizeOutlinePhrase(mandatory);
  if (mTokens.length === 0) return true;
  // deno-lint-ignore no-explicit-any
  return (outline.drills as any[]).some((d: any) => {
    const dn = tokenizeOutlinePhrase(d.name || '');
    const overlap = mTokens.filter((t) => dn.some((x) => x.includes(t) || t.includes(x)));
    return overlap.length >= Math.min(2, mTokens.length) || overlap.length === mTokens.length;
  });
}

/** If the model omitted coach-requested drills, splice minimal outline segments so detail generation still runs on them. */
export function injectMandatoryDrillStubsIntoOutline(plan: GeneratedPracticePlan, mandatories: string[]): void {
  if (!plan.drills) plan.drills = [];
  const insertBeforeCooldown = (): number => {
    // deno-lint-ignore no-explicit-any
    const idx = (plan.drills as any[]).findIndex((d: any) => isCooldownSegment(d) || isDebriefSegment(d));
    return idx === -1 ? plan.drills.length : idx;
  };
  let pos = insertBeforeCooldown();
  for (const raw of mandatories) {
    if (!raw?.trim()) continue;
    const phrase = raw.trim();
    if (outlineCoversMandatoryPhrase(plan, phrase)) continue;
    const stub = {
      name: phrase,
      duration: 12,
      description: `Coach-requested drill: ${phrase}`,
      focus_area: 'Skill Development',
      intensity_level: 'Technique focused' as const,
      position_groups: [{ group_name: 'ALL', stepCount: 3 }],
    };
    // deno-lint-ignore no-explicit-any
    (plan.drills as any[]).splice(pos, 0, stub);
    pos += 1;
  }
}
